/**
 * CareJourneyFlow — "Who is on the road with you": the family's ACTUAL
 * care team merging on and off the road.
 *
 * Vertical road = the family's journey through time (top = earliest event,
 * bottom = Today). Positions come from real start/end dates when entered;
 * undated services sit lower with "Start date not entered" — never implying
 * a date. Line STYLE (not just color) encodes status:
 *   solid  → Active (merges at its start point, continues to Today)
 *   dashed → Paused
 *   dotted → Referred / planned (approaches, never merges until Active)
 *   gray   → Ended (stops before Today, exits with an off-ramp)
 * Prior episodes from `history` render as their own ended lanes, so a
 * service that ends and restarts shows BOTH runs.
 * Labels word-wrap (up to 2 lines) and are anchored inward — never clipped.
 */
import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { SERVICES } from "@/lib/roadmapStages";

// Calm, cheerful tones only — no browns, reds, or dark muddy colors.
const TONE_HEX = {
    teal: "#2C7A8C",
    coral: "#5B9BD5",
    lavender: "#9B8CDE",
    sand: "#6FAE8F",
    sage: "#56A88C",
};
const ENDED_GRAY = "#9AA5B1";

const W = 460;
const ROAD_X = W / 2;
const TOP = 40;
const BOTTOM = 58;

const norm = (s) => (s === "starting_soon" ? "referred" : s);
const STATUS_LABEL = { referred: "Referred", active: "Active", paused: "Paused", ended: "Ended" };

function fmtDate(d) {
    if (!d) return "";
    const dt = new Date(`${String(d).slice(0, 10)}T12:00:00`);
    if (Number.isNaN(dt.getTime())) return String(d);
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function wrapText(str, maxChars, maxLines = 2) {
    const words = String(str || "").trim().split(/\s+/);
    const lines = [];
    let cur = "";
    let overflow = false;
    for (const w of words) {
        const candidate = cur ? `${cur} ${w}` : w;
        if (candidate.length <= maxChars) {
            cur = candidate;
        } else if (lines.length + 1 < maxLines) {
            if (cur) lines.push(cur);
            cur = w.length > maxChars ? w.slice(0, maxChars) : w;
        } else {
            overflow = true;
            break;
        }
    }
    if (cur) lines.push(cur);
    if (!lines.length) lines.push(String(str || "").slice(0, maxChars));
    if (overflow) lines[lines.length - 1] = `${lines[lines.length - 1].slice(0, maxChars - 1)}…`;
    return lines;
}

function dateLineFor(it) {
    if (it.status === "referred") return it.start_date ? `Expected ${fmtDate(it.start_date)}` : "Not started yet";
    if (it.status === "ended") {
        const bits = [];
        if (it.start_date) bits.push(`Started ${fmtDate(it.start_date)}`);
        if (it.end_date) bits.push(`Ended ${fmtDate(it.end_date)}`);
        return bits.join(" · ") || "Dates not entered";
    }
    if (!it.start_date) return "Start date not entered";
    return `${it.restarted ? "Restarted" : "Started"} ${fmtDate(it.start_date)}`;
}

/** One lane per service EPISODE — prior ended runs from `history` are
 *  preserved as their own gray lanes, never erased by a restart. */
function buildItems(providers) {
    const items = [];
    providers.forEach((p) => {
        const pastRuns = (p.history || []).filter((h) => h.status === "ended");
        pastRuns.forEach((h, i) => {
            items.push({
                id: `${p.id}-ep${i}`, providerId: p.id, p,
                name: p.name, service_key: p.service_key,
                status: "ended",
                start_date: h.start_date || "",
                end_date: h.end_date || String(h.ended_at || "").slice(0, 10),
                episode: true,
            });
        });
        const status = norm(p.status);
        items.push({
            id: p.id, providerId: p.id, p,
            name: p.name, service_key: p.service_key,
            status,
            start_date: p.start_date || "",
            end_date: p.end_date || (status === "ended" ? String(p.ended_at || "").slice(0, 10) : ""),
            restarted: pastRuns.length > 0 && status !== "ended",
        });
    });
    return items;
}

function buildLayout(providers) {
    const items = buildItems(providers);
    items.forEach((it) => {
        const type = SERVICES[it.service_key]?.name || it.p.service_name || "Service";
        it.nameLines = wrapText(it.name, 24);
        it.metaLines = wrapText(`${type} · ${STATUS_LABEL[it.status] || it.status}`, 30);
        it.dateLine = dateLineFor(it);
        it.blockH = Math.max(50, 14 + it.nameLines.length * 13 + it.metaLines.length * 10 + 14);
    });

    // Chronological events: dated first (real order), undated near the
    // bottom so route position never implies a date that wasn't entered.
    const evs = [];
    items.forEach((it, i) => evs.push({ type: "start", it, k: `${it.start_date || "9998"}~${String(i).padStart(3, "0")}` }));
    items.forEach((it, i) => {
        if (it.status === "ended") evs.push({ type: "end", it, k: `${it.end_date || it.start_date || "9998"}~${String(500 + i)}` });
    });
    evs.sort((a, b) => (a.k < b.k ? -1 : 1));
    for (let i = 0; i < evs.length; i++) {
        if (evs[i].type !== "end") continue;
        const si = evs.findIndex((e) => e.type === "start" && e.it === evs[i].it);
        if (si > i) {
            const [e] = evs.splice(i, 1);
            evs.splice(si, 0, e);
            i--;
        }
    }

    // Height hugs the content — no big empty stretches for small teams.
    let y = TOP;
    evs.forEach((ev) => {
        ev.y = y + 12;
        y += ev.type === "start" ? ev.it.blockH : 30;
    });
    const H = y + BOTTOM;
    const todayY = H - 40;

    const mergers = items.filter((it) => it.status !== "referred");
    const roadW = Math.max(22, mergers.length * 6 + 12);

    const lanes = items.map((it) => {
        const se = evs.find((e) => e.type === "start" && e.it === it);
        const sIdx = evs.indexOf(se);
        const side = sIdx % 2 === 0 ? "left" : "right";
        const x0 = side === "left" ? 172 : W - 172;
        const slot = mergers.indexOf(it);
        const laneX = slot === -1 ? ROAD_X : ROAD_X + (slot - (mergers.length - 1) / 2) * 6;
        const color = it.status === "ended" ? ENDED_GRAY : (TONE_HEX[SERVICES[it.service_key]?.tone] || "#2C7A8C");
        const lane = { it, sy: se.y, side, x0, laneX, color };
        if (it.status === "ended") {
            const ee = evs.find((e) => e.type === "end" && e.it === it);
            const eIdx = evs.indexOf(ee);
            lane.ey = ee.y;
            lane.exitSide = eIdx % 2 === 0 ? "left" : "right";
            lane.exitX = lane.exitSide === "left" ? 172 : W - 172;
        }
        return lane;
    });
    return { lanes, H, todayY, roadW };
}

function LaneLabel({ lane, selected }) {
    const { it, sy, side, x0, color } = lane;
    const anchor = side === "left" ? "end" : "start";
    const tx = side === "left" ? x0 - 10 : x0 + 10;
    let yy = sy + 3;
    const rows = [];
    it.nameLines.forEach((ln, i) => {
        rows.push(
            <text key={`n${i}`} x={tx} y={yy} textAnchor={anchor} fontSize="11" fontWeight="600"
                fill={selected ? color : "#1F2624"}>
                {ln}
            </text>
        );
        yy += 13;
    });
    it.metaLines.forEach((ln, i) => {
        rows.push(
            <text key={`m${i}`} x={tx} y={yy} textAnchor={anchor} fontSize="8.5" fill="#4C5551">
                {ln}
            </text>
        );
        yy += 10;
    });
    rows.push(
        <text key="d" x={tx} y={yy} textAnchor={anchor} fontSize="8" fontStyle="italic" fill="#6E7A74">
            {it.dateLine}
        </text>
    );
    return <g>{rows}</g>;
}

function Lane({ lane, idx, selected, dimmed, todayY, roadHalf, onSelect }) {
    const { it, sy, x0, laneX, color, exitX, ey } = lane;
    const joinY = sy + 26;
    const groupProps = {
        onClick: () => onSelect(it.providerId),
        style: { cursor: "pointer", opacity: dimmed ? 0.25 : 1, transition: "opacity .25s" },
        "data-testid": `care-flow-lane-${it.id}`,
    };
    const strokeW = selected ? 4 : 2.5;
    const delay = 0.15 + idx * 0.1;
    const hitX = lane.side === "left" ? 4 : Math.min(laneX, x0) - 6;
    const hitW = lane.side === "left" ? Math.max(laneX, x0) + 6 - 4 : W - 4 - hitX;

    if (it.status === "referred") {
        const edgeX = lane.side === "left" ? ROAD_X - roadHalf - 7 : ROAD_X + roadHalf + 7;
        const approach = `M ${x0} ${sy} C ${x0 + (edgeX - x0) * 0.4} ${sy}, ${edgeX - (edgeX - x0) * 0.15} ${sy + 16}, ${edgeX} ${sy + 20}`;
        return (
            <g {...groupProps}>
                <circle cx={x0} cy={sy} r="3.5" fill={color} />
                <path d={approach} fill="none" stroke={color} strokeWidth={strokeW} strokeLinecap="round"
                    strokeDasharray="1.5 6" className="care-flow-incoming" />
                <circle cx={edgeX} cy={sy + 20} r="2.5" fill="none" stroke={color} strokeWidth="1.5" strokeDasharray="2 2" />
                <LaneLabel lane={lane} selected={selected} />
                <rect x={hitX} y={sy - 14} width={hitW} height={it.blockH} fill="transparent" />
            </g>
        );
    }

    const mergeOn = `M ${x0} ${sy} C ${x0 + (laneX - x0) * 0.45} ${sy}, ${laneX} ${sy + 8}, ${laneX} ${joinY}`;
    const stripeEnd = it.status === "ended" ? ey - 8 : todayY - 8;
    const offRamp = it.status === "ended"
        ? `M ${laneX} ${ey - 8} C ${laneX} ${ey + 6}, ${exitX + (laneX - exitX) * 0.5} ${ey + 12}, ${exitX} ${ey + 16}`
        : null;

    return (
        <g {...groupProps}>
            <circle cx={x0} cy={sy} r="3.5" fill={color} />
            <motion.path d={mergeOn} fill="none" stroke={color} strokeWidth={strokeW} strokeLinecap="round"
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay, duration: 0.5, ease: "easeOut" }} />
            <motion.line x1={laneX} y1={joinY} x2={laneX} y2={stripeEnd}
                stroke={color} strokeWidth={selected ? 4.5 : 3} strokeLinecap="round"
                strokeDasharray={it.status === "paused" ? "4 6" : undefined}
                opacity={it.status === "paused" ? 0.75 : 0.95}
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                transition={{ delay: delay + 0.3, duration: 0.55, ease: "easeOut" }} />
            {offRamp && (
                <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: delay + 0.7 }}>
                    <path d={offRamp} fill="none" stroke={color} strokeWidth={strokeW} strokeLinecap="round" opacity="0.85" />
                    <circle cx={exitX} cy={ey + 16} r="3.5" fill="#fff" stroke={color} strokeWidth="2" />
                    <text x={lane.exitSide === "left" ? exitX - 10 : exitX + 10} y={ey + 19}
                        textAnchor={lane.exitSide === "left" ? "end" : "start"} fontSize="8" fill="#6E7A74" fontStyle="italic">
                        {`ended${it.end_date ? ` ${fmtDate(it.end_date)}` : ""}`}
                    </text>
                </motion.g>
            )}
            <LaneLabel lane={lane} selected={selected} />
            <rect x={hitX} y={sy - 14} width={hitW} height={it.blockH} fill="transparent" />
        </g>
    );
}

// Line STYLE carries the meaning — never color alone.
const LEGEND = [
    { key: "active", label: "Solid line — Active", swatch: <span className="inline-block w-6 h-[3px] rounded-full bg-[#2C7A8C]" /> },
    { key: "paused", label: "Dashed line — Paused", swatch: <span className="inline-block w-6 h-[3px] rounded-full" style={{ background: "repeating-linear-gradient(90deg,#64748B 0 5px,transparent 5px 9px)" }} /> },
    { key: "referred", label: "Dotted line — Referred / planned", swatch: <span className="inline-block w-6 h-[3px] rounded-full" style={{ background: "repeating-linear-gradient(90deg,#64748B 0 2px,transparent 2px 7px)" }} /> },
    { key: "ended", label: "Gray line — Ended", swatch: <span className="inline-block w-6 h-[3px] rounded-full" style={{ backgroundColor: ENDED_GRAY }} /> },
];

export default function CareJourneyFlow({ providers, selectedId, onSelect }) {
    const { lanes, H, todayY, roadW } = useMemo(() => buildLayout(providers), [providers]);

    return (
        <div data-testid="care-journey-flow">
            <style>{`@keyframes careFlowDash { to { stroke-dashoffset: -30; } } .care-flow-incoming { animation: careFlowDash 1.6s linear infinite; } @keyframes careFlowTodayPulse { 0%,100% { r: 7px; opacity: .25; } 50% { r: 12px; opacity: .08; } } .care-flow-today-pulse { animation: careFlowTodayPulse 2.4s ease-in-out infinite; }`}</style>

            <div className="flex flex-wrap gap-x-4 gap-y-1.5 justify-center mb-1" data-testid="care-flow-legend">
                {LEGEND.map(({ key, label, swatch }) => (
                    <span key={key} className="inline-flex items-center gap-1.5 text-[10px] text-[#4C5551]">{swatch}{label}</span>
                ))}
            </div>

            <div className="max-w-[540px] mx-auto">
                <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img"
                    aria-label="Providers and programs on your family's road, positioned by their start and end dates"
                    data-testid="care-flow-svg">
                    <rect x={ROAD_X - roadW / 2} y={TOP - 26} width={roadW} height={todayY - (TOP - 26)} rx={roadW / 2} fill="#F7EDC0" />
                    <line x1={ROAD_X} y1={TOP - 10} x2={ROAD_X} y2={todayY - 16} stroke="#fff" strokeWidth="2" strokeDasharray="5 8" strokeLinecap="round" />
                    <text x={ROAD_X} y={TOP - 32} textAnchor="middle" fontSize="8.5" fill="#6E7A74" letterSpacing="1.5" fontWeight="600">YOUR FAMILY&apos;S ROAD</text>

                    {lanes.map((lane, i) => (
                        <Lane key={lane.it.id}
                            lane={lane}
                            idx={i}
                            selected={selectedId === lane.it.providerId}
                            dimmed={selectedId ? selectedId !== lane.it.providerId : false}
                            todayY={todayY}
                            roadHalf={roadW / 2}
                            onSelect={(id) => onSelect(selectedId === id ? null : id)}
                        />
                    ))}

                    <g data-testid="care-flow-today">
                        <circle cx={ROAD_X} cy={todayY} r="7" fill="#2C7A8C" className="care-flow-today-pulse" />
                        <circle cx={ROAD_X} cy={todayY} r="4.5" fill="#2C7A8C" stroke="#fff" strokeWidth="2" />
                        <text x={ROAD_X} y={todayY + 20} textAnchor="middle" fontSize="10" fontWeight="600" fill="#2C7A8C">Today</text>
                    </g>
                </svg>
            </div>

            <p className="mt-1.5 text-[11px] text-[#6E7A74] text-center">
                Tap any lane to see its details and update status, dates, or notes. Services can merge back on at any time.
            </p>
        </div>
    );
}
